import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { noop } from 'lodash';
import { useCallback, useState } from 'react';
import { NextControl, PreviousControl } from '../../../components/Image/Controls/index.js';
import { Container } from '../../../components/Image/Slider/components/Container.js';
import { ControlContainer } from '../../../components/Image/Slider/components/ControlContainer.js';
import { Image } from '../../../components/Image/Slider/components/Image.js';
import { ImageGrid } from '../../../components/Image/Slider/components/ImageGrid.js';
import { VirtualizedSwipeableViews } from '../../../components/Image/Slider/components/VirtualizedSwipeableViews.js';
import { AspectRatio, Fade } from '../../../primitives/index.js';
const getSlideImages = (slideIndex, images, imagesPerSlide) => {
  const offset = slideIndex * imagesPerSlide;
  const slideImages = [];
  for (let i = 0; i < imagesPerSlide; i++) {
    const index = offset + i;
    // Note that src can be empty if the last slide contains less images than imagesPerSlide.
    // This is intentional since we want to render placeholder elements for those missing images.
    const src = images[index];
    slideImages.push({
      index,
      src
    });
  }
  return slideImages;
};
export const Slider = ({
  images,
  activeImage,
  rounded,
  hoverable,
  spacing,
  onClickPrev,
  onClickNext,
  onClickImage = noop,
  // internal
  slideIndex,
  imagesPerSlide,
  aspectRatio,
  onChangeSlideIndex
}) => {
  const [isSwiping, setIsSwiping] = useState(false);
  const [hovering, setHovering] = useState(false);
  const handleMouseEnter = useCallback(() => {
    setHovering(true);
  }, []);
  const handleMouseLeave = useCallback(() => {
    setHovering(false);
  }, []);
  const handleSwitching = useCallback((_, type) => {
    if (type === 'move') {
      setIsSwiping(true);
    }
  }, []);
  const handleTransitionEnd = useCallback(() => {
    setIsSwiping(false);
  }, []);
  const handleClickImage = useCallback(imageIndex => () => {
    if (!isSwiping) {
      onClickImage(imageIndex);
    }
  }, [isSwiping, onClickImage]);
  const slideRenderer = useCallback(({
    index,
    key
  }) => _jsx(ImageGrid, {
    spacing: spacing,
    children: getSlideImages(index, images, imagesPerSlide).map(({
      index: imageIndex,
      src
    }) => src ? _jsx(AspectRatio, {
      ratio: aspectRatio ? aspectRatio[0] / aspectRatio[1] : 3 / 2,
      children: _jsx(Image, {
        src: src,
        active: imageIndex === activeImage,
        draggable: "false",
        alt: "",
        rounded: rounded,
        hoverable: hoverable,
        onClick: handleClickImage(imageIndex)
      })
    }, imageIndex) : _jsx("div", {}, `placeholder-${imageIndex}`))
  }, key), [activeImage, images, imagesPerSlide, rounded, hoverable, handleClickImage]);
  const slideCount = Math.ceil(images.length / imagesPerSlide);
  const hasSwipedBeyondStart = slideIndex > 0;
  const hasSwipedToEnd = slideIndex < slideCount - 1;
  return _jsxs(Container, {
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    children: [_jsx(VirtualizedSwipeableViews, {
      index: slideIndex,
      onChangeIndex: onChangeSlideIndex,
      overscanSlideBefore: 1,
      overscanSlideAfter: 1,
      slideCount: slideCount,
      slideRenderer: slideRenderer,
      onSwitching: handleSwitching,
      onTransitionEnd: handleTransitionEnd,
      enableMouseEvents: true,
      resistance: true
    }), _jsx(Fade, {
      in: hasSwipedBeyondStart,
      children: _jsx(ControlContainer, {
        position: 'left',
        children: _jsx(PreviousControl, {
          highlighted: hovering,
          onClick: onClickPrev
        })
      })
    }), _jsx(Fade, {
      in: hasSwipedToEnd,
      children: _jsx(ControlContainer, {
        position: 'right',
        children: _jsx(NextControl, {
          highlighted: hovering,
          onClick: onClickNext
        })
      })
    })]
  });
};